import React, { useMemo } from 'react';
import { expandUrl } from '@tanium/expand-url';
import { Box } from '@tanium/react-box';
import { Button } from '@tanium/react-button';
import styled from '@tanium/react-emotion-9';
import { CommunityIcon, DataSheetIcon, ReleaseNotesIcon, VoltIcon } from '@tanium/react-graphics';
import { useHomePageSection } from '@tanium/react-home-page-section-context';
import { Stack, StackChild } from '@tanium/react-stack';
import { useTheme } from '@tanium/react-theme-context';
import { css } from '@tanium/style-system';
import { ThemeFontWeights } from '@tanium/theme-data';
import { useTranslation } from './localization';
import {
  BannerIcon,
  ContainerBorder,
  ContainerFill,
  ContainerShadow,
  HeadingText,
  SummaryText,
} from './themeDefinitions';
import { WelcomeLink, WelcomeLinks } from './WelcomeLinks';

export interface WelcomeBannerProps {
  /**
   * The summary header. Should be a short tagline that explains what the
   * product is and how it can be used.
   */
  header: React.ReactNode;
  /**
   * The body of the summary. Should be a short paragraph about the product.
   */
  summary: React.ReactNode;
  /**
   * The link to your product's user guide, typically on https://docs.tanium.com/.
   */
  userGuideLinkUrl?: string;
  /**
   * The link to your product's community page, typically on https://community.tanium.com/.
   */
  communityLinkUrl?: string;
  /**
   * The link to your product's release notes, typically on https://kb.tanium.com/.  Hidden on TaaS.
   */
  releaseNotesLinkUrl?: string;
  /**
   * The link to your product's data sheets.
   */
  dataSheetLinkUrl?: string;
  /**
   * An array of `WelcomeLink` to render the links in the banner.
   * Each object can contain a string of `text` an `href` and an 'icon'.
   */
  additionalLinks?: WelcomeLink[];
  unstable_userGuideLinkText?: string;
  unstable_communityLinkText?: string;
  unstable_releaseNotesLinkText?: string;
  unstable_dataSheetLinkText?: string;
  unstable_hideLabel?: string;
}

const Header = styled('h3')(
  css({
    fontSize: 18,
    fontWeight: ThemeFontWeights.Bold,
    lineHeight: '24px',
    margin: 0,
    color: HeadingText,
  }),
);

const Summary = styled('div')(
  css({
    fontSize: 16,
    lineHeight: '21px',
    color: SummaryText,
  }),
);

export const WelcomeBanner = ({
  header,
  summary,
  userGuideLinkUrl,
  communityLinkUrl,
  releaseNotesLinkUrl,
  dataSheetLinkUrl,
  additionalLinks,
  unstable_userGuideLinkText,
  unstable_communityLinkText,
  unstable_releaseNotesLinkText,
  unstable_dataSheetLinkText,
  unstable_hideLabel,
}: WelcomeBannerProps) => {
  const { t } = useTranslation();
  const { promptHideSection } = useHomePageSection();
  const isTaas = !!window.head_conf?.console?.is_taas;
  const theme = useTheme();

  const userGuideLinkText = unstable_userGuideLinkText ?? t('User Guide');
  const communityLinkText = unstable_communityLinkText ?? t('Community');
  const releaseNotesLinkText = unstable_releaseNotesLinkText ?? t('Release Notes');
  const dataSheetLinkText = unstable_dataSheetLinkText ?? t('Data Sheet');
  const hideLabel = unstable_hideLabel ?? t('Hide');

  const links = useMemo(
    () =>
      [
        userGuideLinkUrl && {
          text: userGuideLinkText,
          href: expandUrl({ path: userGuideLinkUrl, query: isTaas ? { cloud: true } : {} }),
          icon: VoltIcon,
        },
        communityLinkUrl && {
          text: communityLinkText,
          href: communityLinkUrl,
          icon: CommunityIcon,
        },
        releaseNotesLinkUrl &&
          !isTaas && {
            text: releaseNotesLinkText,
            href: releaseNotesLinkUrl,
            icon: ReleaseNotesIcon,
          },
        dataSheetLinkUrl && {
          text: dataSheetLinkText,
          href: dataSheetLinkUrl,
          icon: DataSheetIcon,
        },
        ...(additionalLinks || []),
      ].filter(Boolean) as WelcomeLink[],
    [
      additionalLinks,
      communityLinkUrl,
      dataSheetLinkUrl,
      isTaas,
      releaseNotesLinkUrl,
      communityLinkText,
      dataSheetLinkText,
      releaseNotesLinkText,
      userGuideLinkText,
      userGuideLinkUrl,
    ],
  );

  const BannerImage = useMemo(
    () =>
      styled(BannerIcon(theme))({
        display: 'block',
        width: 170,
        height: 115,
      }),
    [theme],
  );

  return (
    <Stack
      minHeight="130px"
      itemSpacing={1}
      backgroundColor={ContainerFill}
      border={1}
      borderRadius={1}
      p={1}
      borderColor={ContainerBorder}
      boxShadow={ContainerShadow}
    >
      <StackChild grow={0} basis="225px" shrink={0} alignSelf="center">
        <Box px={1}>
          <BannerImage />
        </Box>
      </StackChild>
      <StackChild grow={1}>
        <Stack py={2} direction="column" itemSpacing={2}>
          <Stack direction="column">
            <Header>{header}</Header>
            <Summary>{summary}</Summary>
          </Stack>
          {!!links.length && <WelcomeLinks links={links} />}
        </Stack>
      </StackChild>
      {promptHideSection && (
        <Button size="sm" variant="secondary" onClick={promptHideSection}>
          {hideLabel}
        </Button>
      )}
    </Stack>
  );
};
